(ns io.daskul.tutorial.webapp.views.session-attendee-edit
  "Custom SessionAttendee edit details card — Story 6.3 (feature 0012).

   Adds two composite-action buttons above the standard edit form:
     [Reschedule]      visible when SA :state = :scheduled
     [Schedule Makeup] visible when SA :state = :no-show or :cancelled

   These are NOT FSM transitions on the SA — they invoke the Story 6.1 / 6.2
   domain handlers (POST /api/session-attendees/:id/reschedule and
   /schedule-makeup respectively), which atomically create a new SA on a
   different Session and patch bidirectional :data.reschedule / :data.makeup
   pointers.

   Both buttons open the same Session-picker modal which lists Sessions in the
   admin's current school (?branch-id from the parent Session's branch_id),
   FILTERS OUT :cancelled and :completed Sessions per the Q3 UX rule (belt +
   suspenders with the server-side guard), and submits to the appropriate
   endpoint based on which button opened it.

   On success, redirects back to the parent Session's detail page (so admin
   sees the cascade results)."
  (:require
   [clojure.string :as str]
   [re-frame.core :as rf]
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.aggregates.subs :as agg.subs]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.notifications :as notif]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.form.field :as ff]))

;; ============================================================================
;; Session-picker modal
;; ============================================================================

(defn- format-session-label
  "Best-effort human label for a Session: its wall-clock date + start time
   (#590) + program name (if the include resolved) — falls back to just the
   date/time when the program is absent."
  [session]
  (let [date (:date session)
        start (:start-time session)
        when-str (cond
                   (and date start) (str date " " (subs start 0 5))
                   date date
                   :else "(no start)")
        program (or (get-in session [:program :name])
                    (get-in session [:program :title]))]
    (str when-str
         (when program (str " — " program)))))

(defn- session-eligible?
  "Q3 UX rule: only :scheduled Sessions are valid targets. Belt + suspenders
   with the server-side guard (which also rejects :cancelled / :completed)."
  [session]
  (= "scheduled" (:state session)))

(defui session-picker-modal
  "Modal that lists all Sessions in the same school as the current SA and lets
   the admin pick one. Filters out :cancelled and :completed.

   Props:
   - title:       string — modal heading
   - branch-id:   uuid   — used to scope the listing
   - exclude-ids: set    — Session ids to hide (e.g., the SA's own session)
   - on-select:   (fn [session])
   - on-close:    (fn [])"
  [{:keys [title branch-id exclude-ids on-select on-close]}]
  (let [[items set-items!] (uix/use-state [])
        [loading? set-loading!] (uix/use-state true)
        [error set-error!] (uix/use-state nil)

        _ (uix/use-effect
           (fn []
             (set-loading! true)
             (set-error! nil)
             (api/request!
              {:method :get
               :path "/sessions"
               :params (cond-> {:page-size 100}
                         branch-id (assoc :branch-id branch-id))
               :on-success (fn [response]
                             (let [all (or (:items response) [])
                                   eligible (->> all
                                                 (filter session-eligible?)
                                                 (remove #(contains? (or exclude-ids #{}) (:id %))))]
                               (set-items! eligible)
                               (set-loading! false)))
               :on-error (fn [_err]
                           (set-error! "Failed to load sessions")
                           (set-loading! false))})
             js/undefined)
           [branch-id exclude-ids])]
    ($ ui/modal
      {:opened true
       :onClose on-close
       :title title
       :centered true
       :size "md"}
      ($ ui/stack {:gap "md"}
        (cond
          loading?
          ($ ui/centered-loader {:label "Loading sessions..."})

          error
          ($ ui/error-alert error)

          (empty? items)
          ($ ui/empty-alert {:title "No eligible sessions"}
            "No :scheduled sessions available. Cancel or completed sessions are filtered out — pick a session that is still scheduled.")

          :else
          ($ ui/stack {:gap "xs"}
            (for [s items]
              ($ ui/paper
                {:key (:id s)
                 :p "sm"
                 :withBorder true
                 :style {:cursor "pointer"}
                 :onClick #(on-select s)}
                ($ ui/text {:size "sm"} (format-session-label s))))))))))

;; ============================================================================
;; Submit helpers — POST to the Story 6.1 / 6.2 endpoints.
;; ============================================================================

;; Register a tiny reg-event-fx so we can fire :notification from outside
;; the standard aggregate flow. (The :notification fx is registered in
;; tutorial/webapp/effects.cljs.)
(rf/reg-event-fx
 ::notify
 (fn [_ [_ notif-map]]
   {:notification notif-map}))

(defn- submit-composite!
  "POST to /session-attendees/:id/<endpoint> with {:new-session-id ...}.
   On success, refreshes the SA + parent session and surfaces a toast.
   On failure, surfaces an error toast."
  [{:keys [endpoint sa-id new-session-id parent-session-id success-msg]}]
  (api/request!
   {:method :post
    :path (str "/session-attendees/" sa-id "/" endpoint)
    :params {:new-session-id new-session-id}
    :on-success (fn [_resp]
                  (rf/dispatch [::notify (notif/success "Success" success-msg)])
                  (when sa-id
                    (rf/dispatch [::agg.events/get :session-attendee sa-id]))
                  (when parent-session-id
                    (rf/dispatch [::agg.events/get :session parent-session-id])))
    :on-error (fn [{:keys [response]}]
                (let [msg (or (get-in response [:error/message])
                              (get-in response [:error :message])
                              "Operation failed")]
                  (rf/dispatch [::notify (notif/error "Failed" msg)])))}))

;; ============================================================================
;; Composite action panel
;; ============================================================================

(defui composite-actions-panel
  "Renders Reschedule / Schedule Makeup buttons + the picker modal.

   Visibility rule (matches the Q3 UX guard + server guard):
     :scheduled                  → [Reschedule]
     :no-show / :cancelled       → [Schedule Makeup]
     anything else (:attended)   → no buttons (admin un-marks first)

   Props:
   - sa:        full SA record (for :state, :session-id)
   - branch-id: parent Session's branch (used to scope picker)"
  [{:keys [sa branch-id]}]
  (let [state (:state sa)
        sa-id (:id sa)
        parent-session-id (:session-id sa)
        ;; modal-mode: nil | :reschedule | :schedule-makeup
        [modal-mode set-modal-mode!] (uix/use-state nil)
        scheduled? (= "scheduled" state)
        eligible-for-makeup? (#{"no-show" "cancelled"} state)
        handle-pick (fn [target-session]
                      (case modal-mode
                        :reschedule
                        (submit-composite!
                         {:endpoint "reschedule"
                          :sa-id sa-id
                          :new-session-id (:id target-session)
                          :parent-session-id parent-session-id
                          :success-msg "Attendee rescheduled."})
                        :schedule-makeup
                        (submit-composite!
                         {:endpoint "schedule-makeup"
                          :sa-id sa-id
                          :new-session-id (:id target-session)
                          :parent-session-id parent-session-id
                          :success-msg "Makeup scheduled."})
                        nil)
                      (set-modal-mode! nil))]
    (when (or scheduled? eligible-for-makeup?)
      ($ :<>
        ($ ui/paper {:p "md" :withBorder true :mb "md"}
          ($ ui/stack {:gap "sm"}
            ($ ui/text {:fw 500 :size "sm" :c "dimmed"} "Composite Actions")
            ($ ui/group {:gap "sm"}
              (when scheduled?
                ($ ui/button
                  {:variant "primary"
                   :size "sm"
                   :onClick #(set-modal-mode! :reschedule)}
                  "Reschedule"))
              (when eligible-for-makeup?
                ($ ui/button
                  {:variant "primary"
                   :size "sm"
                   :onClick #(set-modal-mode! :schedule-makeup)}
                  "Schedule Makeup")))))
        (when modal-mode
          ($ session-picker-modal
            {:title (case modal-mode
                      :reschedule "Pick a Session to reschedule onto"
                      :schedule-makeup "Pick a Session for the makeup"
                      "Pick a Session")
             :branch-id branch-id
             :exclude-ids #{parent-session-id}
             :on-select handle-pick
             :on-close #(set-modal-mode! nil)}))))))

;; ============================================================================
;; Custom edit details card
;; ============================================================================

(defui details-card
  "Custom SA edit form card. Renders the standard edit form (delegated to a
   thin re-implementation of the framework's default) plus a composite-actions
   panel above it.

   Props (passed by the framework's aggregate-edit):
     agg-key, config, schema, fields, form-data, set-field!, submit!,
     saving?, error, loading?, read-only?, delete-enabled?,
     open-delete-modal!, handle-cancel"
  [{:keys [agg-key config schema fields form-data set-field! submit!
           saving? error loading? read-only? delete-enabled?
           open-delete-modal! handle-cancel]}]
  (let [sa (urf/use-subscribe [::agg.subs/current agg-key])
        branch-id (or (get-in sa [:session :branch-id])
                      (get-in sa [:session :branch_id]))]
    ($ ui/stack {:gap "md"}
      ;; Story 6.3 panel — visible only when current state allows.
      (when sa
        ($ composite-actions-panel
          {:sa sa
           :branch-id branch-id}))

      ;; Standard form card (mirrors the framework default).
      ($ ui/card {:shadow "sm" :padding #js {:base "sm" :sm "lg"} :radius "md" :withBorder true}
        (when read-only?
          ($ ui/info-alert {:mb "md"}
            (str "This " (str/lower-case (or (:label-singular config) "record"))
                 " is " (:state form-data) " and cannot be edited.")))

        (when-let [form-error (ff/form-level-error-message error fields)]
          ($ ui/error-alert {:mb "md"} form-error))

        (cond
          loading?
          ($ ui/centered-loader)

          (not schema)
          ($ ui/warning-alert {:title "Loading schema..."}
            "Fetching form configuration...")

          :else
          ($ :form {:onSubmit submit!}
            ($ ui/stack {:gap "md"}
              (for [field fields]
                (let [field-key (:name field)]
                  ($ ff/form-field
                    {:key (name field-key)
                     :agg-key agg-key
                     :field field
                     :value (get form-data field-key)
                     :on-change #(set-field! field-key %)
                     :disabled (or read-only? (:readonly field))})))

              ($ ui/group {:justify "space-between"}
                (if (and delete-enabled? (not read-only?))
                  ($ ui/button {:variant "subtle" :color "red"
                                :onClick open-delete-modal!}
                    "Delete")
                  ($ :div))
                ($ ui/group {:gap "sm"}
                  ($ ui/button {:variant "subtle" :color "gray"
                                :onClick handle-cancel}
                    "Cancel")
                  (when-not read-only?
                    ($ ui/button {:type "submit" :loading saving?}
                      "Save")))))))))))

